import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { StanInput, StanModal } from '@/components';
import { useSendTestEmail } from '../queries';
import { stanNotify } from '@/lib';

interface TestSendModalProps {
    isOpen: boolean;
    defaultEmail: string;
    onClose: () => void;
    flowId: number;
    emailId?: number;
    subject: string;
    body: string;
}

const testSendSchema = z.object({
    emailAddress: z.string().trim().email('Please enter a valid email address'),
});

type TestSendFormData = z.infer<typeof testSendSchema>;

export const TestSendModal = ({
    isOpen,
    defaultEmail,
    onClose,
    flowId,
    emailId,
    subject,
    body,
}: TestSendModalProps) => {
    const [apiError, setApiError] = useState<string>('');

    // Mutation for sending test email
    const { mutateAsync: sendTestEmail, isPending: isLoading } = useSendTestEmail(flowId, emailId);

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<TestSendFormData>({
        resolver: zodResolver(testSendSchema),
        defaultValues: {
            emailAddress: defaultEmail,
        },
    });

    // Determine if test send is allowed
    const hasSubject = !!subject?.trim();
    const hasBody = !!body?.trim();
    const canSend = hasSubject && hasBody;

    // Generate validation message
    const getValidationMessage = (): string | null => {
        if (!hasSubject) {
            return 'Please add a subject line before sending a test email.';
        }
        if (!hasBody) {
            return 'Please add email content before sending a test email.';
        }
        return null;
    };

    const validationMessage = getValidationMessage();

    const onSubmit = async (data: TestSendFormData) => {
        if (!canSend) {
            return;
        }

        setApiError('');

        try {
            await sendTestEmail({
                emailAddress: data.emailAddress,
                formData: { subject, body, delay: '0', delayUnit: 'hours' },
            });

            stanNotify({
                title: 'Test email sent successfully',
                type: 'success',
            });

            reset({ emailAddress: defaultEmail });
            onClose();
        } catch (err: any) {
            // Keep modal open on error to allow retry
            const status = err.response?.status;

            if (status === 429) {
                setApiError('Too many test emails sent. Please try again later.');
            } else if (status === 400) {
                setApiError('Invalid email address');
            } else {
                setApiError(err.response?.data?.message || 'Failed to send test email. Please try again.');
            }
        }
    };

    const handleClose = () => {
        reset({ emailAddress: defaultEmail });
        setApiError('');
        onClose();
    };

    return (
        <StanModal
            id='test-send-modal'
            isOpen={isOpen}
            onClose={handleClose}
            title='Send Test Email'
            size='sm'
            primaryActionLabel='Send Test'
            primaryActionLoading={isLoading}
            primaryActionDisabled={!canSend || isLoading}
            onPrimaryAction={handleSubmit(onSubmit)}
            secondaryActionLabel='Cancel'
            secondaryActionDisabled={isLoading}
            onSecondaryAction={handleClose}
        >
            {validationMessage ? (
                <div className='border-warning-intense bg-warning-dimmed -mt-2 mb-4 rounded-md border px-4 py-3'>
                    <p className='text-warning-bold text-caption-1-regular text-center'>{validationMessage}</p>
                </div>
            ) : (
                <p className='text-neutral-secondary text-caption-1-regular -mt-2 pb-4 text-center'>
                    Send a test email to verify how it looks before publishing.
                </p>
            )}

            <Controller
                name='emailAddress'
                control={control}
                render={({ field }) => (
                    <StanInput
                        {...field}
                        topLabel='Email Address'
                        error={!!errors.emailAddress || !!apiError}
                        hintText={errors.emailAddress?.message || apiError}
                        placeholder='user@example.com'
                        disabled={isLoading}
                        autoFocus={isOpen}
                        onChange={value => {
                            field.onChange(value);
                            if (apiError) {
                                setApiError('');
                            }
                        }}
                    />
                )}
            />
        </StanModal>
    );
};
